// ============================================
// Material Asset — the serialized `.mat` + its loader
// ============================================
//
// A `.mat` is the authored material instance: it references a shader (.wgsl) and
// carries concrete override/property/texture values. The shader's `@material`
// marker ([[MaterialReflection]]) defines the schema; the .mat fills in values.
//
// This module is engine-side (render2d) and headless: `loadMaterialAsset` is pure
// assembly over the existing `Material` constructor — the editor and (later) the
// native player both feed it resolved shader source + texture handles via callbacks.

import { Material } from './Material';
import { type IRenderContext } from '../core/IRenderContext';
import { type GPUValue } from '../core/GPUTypes';
import {
  type TextureHandle,
  type TextureSlotName,
  type MaterialTag,
  type RenderState,
  type OverrideConstants,
  TextureSlot,
  SPRITE_VERTEX_LAYOUT,
  NULL_HANDLE,
} from '../types';
import {
  reflectWgsl,
  buildSpriteUniformDef,
  materialDefaults,
  type MaterialReflection,
} from './MaterialReflection';

/** Empty reflection used when a shader has no `@material` marker. */
const EMPTY_REFLECTION: MaterialReflection = {
  supports: [], properties: [], overrides: [], textures: [], requires: [], usesTime: false, usesLighting: false,
};

// ----------------------------------------------------------------------------
// Schema
// ----------------------------------------------------------------------------

/** On-disk `.mat` shape. `shader` is a guid/path resolved by the host. */
export interface MaterialAssetData {
  /** Reference (guid or path) to the .wgsl shader asset. */
  shader: string;
  /** Pass routing tag (defaults to the shader marker's tag, else 'transparent'). */
  tag?: MaterialTag;
  /** Per-material render state overrides. */
  renderState?: RenderState;
  /** WGSL `override`-constant values (the variant selection). */
  overrides?: OverrideConstants;
  /** Uniform property values, keyed by property name (matches the marker). */
  properties?: Record<string, GPUValue>;
  /** Texture bindings: slot name → texture guid. */
  textures?: Record<string, string>;
}

/** Current `.mat` format version (for future migration). */
export const MATERIAL_ASSET_VERSION = 1;

/** Callbacks the host provides so the loader stays runtime-agnostic. */
export interface MaterialLoadResolvers {
  /** Resolve a shader ref → plain WGSL source, or null. */
  resolveShaderSource: (shaderRef: string) => string | null;
  /** Resolve a texture guid → GPU handle, or null/undefined if unavailable. */
  resolveTexture?: (guid: string) => TextureHandle | null | undefined;
  /**
   * Precomputed reflection for the uniform layout. REQUIRED for `.slang` shaders: `resolveShaderSource`
   * returns the EMITTED WGSL there, which `reflectWgsl` can't read (mangled names, no `//!`) — so the host
   * must supply the real reflection (reflectSlang). When omitted, the loader falls back to reflectWgsl(source)
   * (correct for plain `.wgsl`). `null` = explicitly no reflection.
   */
  reflection?: MaterialReflection | null;
  /**
   * Fill any declared-but-unassigned texture slot with a 1×1 white default so the material's group(0) bind
   * group always has exactly `textureCount` textures (default true). Without this, a multi-texture material
   * whose `.mat` doesn't assign every slot — or the sprite path which only binds ALBEDO — builds a group(0)
   * that mismatches the pipeline layout → a WebGPU validation error every frame. Set false when the host
   * fills the slots itself (e.g. the material preview, which binds its own backdrop per draw).
   */
  fillUnassignedTextures?: boolean;
}

// ----------------------------------------------------------------------------
// (De)serialization helpers (pure)
// ----------------------------------------------------------------------------

/** Parse `.mat` JSON text into typed data (throws on invalid JSON). */
export function parseMaterialAsset(text: string): MaterialAssetData {
  const o = JSON.parse(text) as Record<string, unknown>;
  if (typeof o.shader !== 'string') {
    throw new Error('.mat is missing a string "shader" reference');
  }
  return o as unknown as MaterialAssetData;
}

/** Serialize `.mat` data to pretty JSON. */
export function serializeMaterialAsset(data: MaterialAssetData): string {
  return JSON.stringify({ version: MATERIAL_ASSET_VERSION, ...data }, null, 2);
}

/** Build a default `.mat` for a shader from its reflection (seeds inspector values). */
export function createDefaultMaterialData(shaderRef: string, reflection: MaterialReflection): MaterialAssetData {
  const { properties, overrides } = materialDefaults(reflection);
  return {
    shader: shaderRef,
    tag: reflection.tag,
    overrides: Object.keys(overrides).length ? overrides : undefined,
    properties: Object.keys(properties).length ? properties : undefined,
  };
}

/**
 * Validate `.mat` values against a shader's reflection. Returns a list of
 * human-readable issues ([] = valid). Used by the importer + inspector.
 */
export function validateMaterialData(data: MaterialAssetData, reflection: MaterialReflection): string[] {
  const issues: string[] = [];
  const propNames = new Set(reflection.properties.map((p) => p.name));
  const overrideNames = new Set(reflection.overrides.map((o) => o.name));
  const slotNames = new Set(reflection.textures.map((t) => t.slot as string));

  for (const name of Object.keys(data.properties ?? {})) {
    if (!propNames.has(name)) issues.push(`Unknown property "${name}" (not declared in shader @material marker)`);
  }
  for (const name of Object.keys(data.overrides ?? {})) {
    if (!overrideNames.has(name)) issues.push(`Unknown override "${name}" (not declared in shader @material marker)`);
  }
  for (const slot of Object.keys(data.textures ?? {})) {
    if (!(slot in TextureSlot)) issues.push(`Unknown texture slot "${slot}"`);
    else if (slotNames.size && !slotNames.has(slot)) issues.push(`Texture slot "${slot}" not declared in shader @material marker`);
  }
  return issues;
}

// ----------------------------------------------------------------------------
// Loader
// ----------------------------------------------------------------------------

/**
 * Remap `.mat` override keys (authored name) → the spec-constant `@id` string when the reflection knows it.
 * Slang emits `@id(N) override NAME_N` (mangled identifier), so WebGPU pipeline constants must be keyed by N;
 * plain-WGSL `override NAME` has no `@id` → stays name-keyed. See {@link MaterialOverride.id}.
 */
function remapOverrideKeys(
  overrides: OverrideConstants | undefined,
  reflection: MaterialReflection | null,
): OverrideConstants | undefined {
  if (!overrides || !reflection) return overrides;
  const idByName = new Map<string, number>();
  for (const o of reflection.overrides) if (o.id !== undefined) idByName.set(o.name, o.id);
  if (idByName.size === 0) return overrides;
  const out: OverrideConstants = {};
  for (const [name, value] of Object.entries(overrides)) {
    const id = idByName.get(name);
    out[id !== undefined ? String(id) : name] = value;
  }
  return out;
}

/** One shared 1×1 white texture per context, for filling unassigned material texture slots. */
const whiteTextureByCtx = new WeakMap<IRenderContext, TextureHandle>();
function getDefaultWhiteTexture(ctx: IRenderContext): TextureHandle {
  let t = whiteTextureByCtx.get(ctx);
  if (t == null) {
    t = ctx.createTexture({ width: 1, height: 1, format: 'rgba8unorm', usage: ['sampled', 'copy-dst'], label: 'mat-default-white' });
    ctx.updateTexture(t, new Uint8Array([255, 255, 255, 255]), 0, 0, 1, 1);
    whiteTextureByCtx.set(ctx, t);
  }
  return t;
}

/**
 * Construct a runtime {@link Material} from `.mat` data. Resolves the shader
 * source + textures via the host callbacks, derives the uniform layout from the
 * shader's `@material` marker, and applies the stored override/property/texture
 * values. Returns null if the shader source can't be resolved.
 *
 * Marker property defaults are applied first, then the .mat's stored values, so a
 * partially-authored .mat still gets sensible uniforms.
 */
export function loadMaterialAsset(
  ctx: IRenderContext,
  data: MaterialAssetData,
  resolvers: MaterialLoadResolvers,
): Material | null {
  const source = resolvers.resolveShaderSource(data.shader);
  if (source == null) return null;

  // Use the host-supplied reflection when given (required for .slang — see MaterialLoadResolvers.reflection);
  // otherwise derive it from the (plain WGSL) source.
  const reflection = resolvers.reflection !== undefined ? resolvers.reflection : reflectWgsl(source);
  // Sprite-batch convention: uniform struct = [uViewProjection, ...properties] (always present so the
  // batch's setUniform('uViewProjection', vp) resolves). See buildSpriteUniformDef.
  const uniformDef = buildSpriteUniformDef(reflection ?? EMPTY_REFLECTION);
  const textureCount = reflection && reflection.textures.length > 0 ? reflection.textures.length : 1;

  // Remap override keys from authored name → spec-constant `@id` (as a string). Slang emits
  // `@id(N) override NAME_N`, so WebGPU pipeline constants must be keyed by N, not the name; plain-WGSL
  // overrides have no id → keyed by name (matches `override NAME`). See MaterialOverride.id.
  const overrides = remapOverrideKeys(data.overrides, reflection);

  const material = new Material(ctx, {
    program: source,
    overrides,
    uniformDef,
    tag: data.tag ?? reflection?.tag,
    renderState: data.renderState,
    textureCount,
    // Sprite-batch vertex layout (pos/uv/color @location 0/1/2) — the material's vs_main reads these,
    // so its pipeline must declare the same vertex state the SpriteBatch uses.
    vertexBuffers: [SPRITE_VERTEX_LAYOUT],
    // `//! requires: lighting` → the shader declares group(1) bindings (via `import engine.materialLit` in
    // Slang) and expects the scene's Forward+ light data bound. The render path supplies it for the forward+ pass.
    hasForwardPlus: reflection?.usesLighting ?? false,
    label: `mat:${data.shader}`,
  });

  // Properties: shader defaults first, then the .mat's explicit values. Filter to fields the shader's
  // uniform struct actually declares — a stale/edited .mat referencing a property the shader no longer has
  // must NOT crash (StructWriter.set throws on an unknown field); skip it instead.
  if (material.properties) {
    const valid = new Set(uniformDef.fields.map((f) => f.name));
    const defaults = reflection ? materialDefaults(reflection).properties : {};
    const merged: Record<string, GPUValue> = { ...defaults, ...(data.properties ?? {}) };
    const filtered: Record<string, GPUValue> = {};
    for (const [k, v] of Object.entries(merged)) if (valid.has(k)) filtered[k] = v;
    if (Object.keys(filtered).length) material.setProperties(filtered);
  }

  // Textures: resolve each slot's guid → handle.
  if (data.textures && resolvers.resolveTexture) {
    for (const [slot, guid] of Object.entries(data.textures)) {
      if (!(slot in TextureSlot)) continue;
      const handle = resolvers.resolveTexture(guid);
      if (handle != null) material.setTexture(slot as TextureSlotName, handle);
    }
  }

  // Fill any declared-but-unassigned slot with white so group(0) always has `textureCount` textures (a
  // multi-texture material — or the sprite path that only binds ALBEDO — would otherwise build a bind group
  // that mismatches the pipeline layout). Hosts that bind their own per-draw textures opt out (see resolvers).
  if (resolvers.fillUnassignedTextures !== false) {
    for (let i = 0; i < textureCount; i++) {
      if (material.getTexture(i) === NULL_HANDLE) material.setTextureSlot(i, getDefaultWhiteTexture(ctx));
    }
  }

  return material;
}
